import { PrinterProfile } from '../../types/printerProfile';
import { PortedTicketData as TicketData } from './types';
import { buildTicketPlan, joinTicketLines, tileSeparator, PlanLine } from './ticketTestPlan';
import { isRasterUsable } from './escposRaster';
import {
  rasterizePlanSlice,
  rasterTopMargin,
  rasterWidthPxFor,
  rasterScaleOf,
  rasterHighlightKeyInfoOf,
  scaledCharsPerLine,
  resolveSeparatorPattern,
  textSafeSeparator,
} from './ticketFormatterRaster';
import { buildNoAnchorTicket, renderNoAnchorLines } from './ticketFormatterNoAnchor';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * TEST 2 — « RASTER HYBRIDE » : seul le BLOC ARTICLES part en image
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * HYPOTHÈSE TESTÉE : l'image n'est nécessaire que sur la ZONE VERROUILLÉE. En-tête,
 * totaux, paiement et pied de ticket ne contiennent ni ligne `ARTICLES` ni motif
 * `x<chiffres>` : le pré-traitement natif les recopie tels quels. Seules les lignes
 * d'articles risquent la réécriture — ce sont donc les seules qu'on met en bitmap.
 *
 * CE QUE ÇA ISOLE : si le TEST 1 sort bien ET le TEST 2 aussi, on sait que le raster
 * n'est requis que sur ~10 % du volume, et l'hybride devient le candidat PRODUCTION
 * réaliste : temps d'impression et mémoire divisés par ~8 face au tout-image, tout en
 * gardant la colonne des prix intacte.
 * Si le TEST 1 sort bien mais pas le TEST 2, c'est que la zone texte subit elle aussi une
 * transformation qu'on n'a pas identifiée — information tout aussi utile.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * POURQUOI LA PARTIE TEXTE EST SÛRE
 * ───────────────────────────────────────────────────────────────────────────────
 * L'en-tête de section `ARTICLES` fait partie du bloc mis en IMAGE : le texte émis par ce
 * module ne contient donc AUCUNE ligne pouvant armer `inArticlesSection`. Tout le bloc de
 * réécriture natif reste inerte, y compris pour la ligne `TOTAL TTC`.
 * La partie texte réutilise exactement le rendu du TEST 4 (colonnes natives `[L]…[R]`),
 * de sorte que les deux modules soient comparables ligne à ligne.
 *
 * ⚠️ Mêmes cibles et même refus explicite que le TEST 1 : image = chemin ESC/POS
 * uniquement. Sur Star ou Sunmi, repli automatique sur le rendu texte du TEST 4.
 */

const widthOf = (profile?: PrinterProfile): number => {
  const w = Number(profile?.config?.width);
  return Number.isFinite(w) && w >= 24 ? Math.min(64, Math.floor(w)) : 48;
};

/**
 * Rend une tranche TEXTE (en-tête ou pied de l'hybride) en faisant RESSORTIR EN IMAGE deux
 * catégories de lignes qui, en texte natif, ne peuvent PAS être agrandies par le pont :
 *   • le NOM DE L'ÉTABLISSEMENT (`big`) → image 2× SANS inversion, TOUJOURS (c'est le correctif
 *     du « nom pas plus gros » : en texte l'en-tête sortait à la taille du corps) ;
 *   • les INFOS CLÉS (`emphasis` : n° de commande / type / table) → image 2× en vidéo inversée,
 *     seulement si la case de mise en évidence est active (`highlightKeyInfo`).
 * Tout le reste part en texte natif `[L]…[R]`. On accumule les lignes texte et on les vide
 * autour de chaque ligne image, en préservant l'ordre. Repli en texte si un rendu image échoue.
 */
const renderTextSliceWithHighlight = (
  lines: PlanLine[],
  width: number,
  paperPx: number,
  emitMarkers: boolean,
  textSep: string,
  imgSep: string,
  highlightKeyInfo: boolean
): string[] => {
  const out: string[] = [];
  let textRun: PlanLine[] = [];
  const flushText = () => {
    if (textRun.length) {
      out.push(...renderNoAnchorLines(textRun, width, emitMarkers, textSep));
      textRun = [];
    }
  };

  for (const ln of lines) {
    const isName = ln.k === 'center' && ln.big;
    const isKey = highlightKeyInfo && ln.k === 'center' && ln.emphasis;
    if (isName || isKey) {
      flushText();
      const img = rasterizePlanSlice([ln], {
        charsPerLine: width,
        widthPx: paperPx,
        emitMarkers,
        separator: imgSep,
        highlightKeyInfo: true,
      });
      // Rendu image OK → bandes ; sinon la ligne repart en texte avec la suite (ordre gardé).
      if (img) out.push(...img);
      else textRun.push(ln);
    } else {
      textRun.push(ln);
    }
  }
  flushText();
  return out;
};

export const buildRasterHybridTicket = (data: TicketData, profile?: PrinterProfile): string => {
  const baseWidth = widthOf(profile);
  // Largeur PHYSIQUE figée par le profil, indépendante de l'échelle.
  const paperPx = rasterWidthPxFor(baseWidth);
  const emitMarkers = profile?.config?.showCategories !== false;

  if (!isRasterUsable()) return buildNoAnchorTicket(data, profile);

  const charsPerLine = scaledCharsPerLine(baseWidth, rasterScaleOf(profile));
  const plan = buildTicketPlan(data, {
    width: charsPerLine,
    quantityStyle: 'x-suffix',
    emitCategoryMarkers: emitMarkers,
  });

  const head = plan.lines.slice(0, plan.itemsFrom);
  const body = plan.lines.slice(plan.itemsFrom, plan.itemsTo);
  const tail = plan.lines.slice(plan.itemsTo);

  // Séparateur exploitant : brut (honoré tel quel) pour la zone IMAGE (bloc articles),
  // version texte-sûre pour les zones tête/pied qui repartent en texte vers le pont.
  const rawSep = resolveSeparatorPattern(profile);
  const imgSep = tileSeparator(rawSep, plan.width);
  const textSep = textSafeSeparator(rawSep, plan.width);

  const bodyLines = rasterizePlanSlice(body, {
    charsPerLine: plan.width,
    widthPx: paperPx,
    emitMarkers,
    separator: imgSep,
  });
  if (!bodyLines) return buildNoAnchorTicket(data, profile);

  // Les infos clés (n° de commande / type / table) sont dans l'EN-TÊTE, qui part en texte :
  // on les fait ressortir en image inversée ligne à ligne quand la case est cochée.
  const highlightKeyInfo = rasterHighlightKeyInfoOf(profile);

  return joinTicketLines([
    // Marge blanche en haut (symétrique de l'espace du bas), en IMAGE pour ne pas être supprimée
    // comme le serait une ligne texte vide de tête.
    ...rasterTopMargin(plan.width, paperPx),
    ...renderTextSliceWithHighlight(head, plan.width, paperPx, emitMarkers, textSep, imgSep, highlightKeyInfo),
    ...bodyLines,
    ...renderTextSliceWithHighlight(tail, plan.width, paperPx, emitMarkers, textSep, imgSep, highlightKeyInfo),
  ]);
};
